import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Modal = ({ 
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className 
}) => {
  // Close on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[90vw]',
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="modal-backdrop"
      />
      
      {/* Modal Content */}
      <div 
        className={cn(
          "relative w-full bg-graphite rounded-xl border border-white/[0.06]",
          "shadow-2xl shadow-black/50 animate-in fade-in zoom-in-95 duration-200",
          sizeClasses[size],
          className
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
          <h2 className="text-xl font-semibold text-cloud-white">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
            data-testid="modal-close"
          >
            <X className="w-5 h-5 text-soft-silver" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {children}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-white/[0.06]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
